import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Bell, CheckCheck } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Shared notification bell. basePath examples:
 *  client: "/notifications"   admin: "/admin/notifications"   clinic: "/clinic/notifications"
 */
export default function NotificationBell({ authHeader, basePath, dark = false }) {
  const [data, setData] = useState({ notifications: [], unread_count: 0 });
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const res = await axios.get(`${API}${basePath}`, { headers: authHeader() });
      setData(res.data);
    } catch {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const readAll = async () => {
    await axios.post(`${API}${basePath}/read`, { all: true }, { headers: authHeader() });
    load();
  };

  const openItem = async (n) => {
    if (!n.read) {
      axios.post(`${API}${basePath}/read`, { ids: [n.id] }, { headers: authHeader() }).then(load);
    }
    if (n.link) {
      setOpen(false);
      navigate(n.link);
    }
  };

  const timeAgo = (iso) => {
    const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
    if (mins < 1) return "now";
    if (mins < 60) return `${mins}m`;
    if (mins < 1440) return `${Math.round(mins / 60)}h`;
    return `${Math.round(mins / 1440)}d`;
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => {
          setOpen(!open);
          if (!open) load();
        }}
        data-testid="notif-bell"
        aria-label="Notifications"
        className={`relative w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
          dark ? "bg-[#F8F7F3]/10 text-[#F8F7F3] hover:bg-[#F8F7F3]/20" : "bg-[#3F5043]/5 text-[#3F5043] hover:bg-[#3F5043]/10"
        }`}
      >
        <Bell size={16} />
        {data.unread_count > 0 && (
          <span
            data-testid="notif-badge"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#875F45] text-[#F8F7F3] text-[10px] font-bold flex items-center justify-center"
          >
            {data.unread_count > 9 ? "9+" : data.unread_count}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notif-panel"
          className="absolute right-0 mt-2 w-80 max-w-[85vw] bg-[#FFFFF8] rounded-2xl shadow-2xl border border-[#BCB3A6]/40 overflow-hidden z-50"
        >
          <div className="flex items-center justify-between px-4 py-3 bg-[#3F5043] text-[#F8F7F3]">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold">Notifications</span>
            {data.unread_count > 0 && (
              <button onClick={readAll} data-testid="notif-read-all"
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-[#AE8F78] hover:text-[#F8F7F3]">
                <CheckCheck size={12} /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {data.notifications.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-[#3F5043]/60">You're all caught up.</div>
            )}
            {data.notifications.map((n, i) => (
              <button
                key={n.id}
                onClick={() => openItem(n)}
                data-testid={`notif-item-${i}`}
                className={`w-full text-left px-4 py-3 border-b border-[#BCB3A6]/20 last:border-0 transition-colors ${
                  n.read ? "opacity-60" : "bg-[#AE8F78]/5 hover:bg-[#AE8F78]/10"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="text-sm font-medium text-[#3F5043] leading-snug">{n.title}</div>
                  <span className="text-[10px] text-[#899480] flex-shrink-0 mt-0.5">{timeAgo(n.created_at)}</span>
                </div>
                {n.body && <div className="text-xs text-[#3F5043]/70 mt-0.5 leading-relaxed">{n.body}</div>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
